import { CheckOutlined } from '@mui/icons-material';
import { Box, TableCell, TableRow, Typography } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { getCanEditCountOfQty } from 'entities/Order/model/selectors/ordersPermissions';
import { getSelectedOrderIsPreSubmission } from 'entities/Order/model/selectors/ordersSelectors';
import { Order } from 'entities/Order/model/types/OrderSchema';
import { HydratedProposal } from 'entities/Proposal/model/types/ProposalSchema';
import { getUserCanSeePrices } from 'entities/User/model/selectors/userPermissionsSeletors';
import { getUserIsMaintenanceTechnician } from 'entities/User/model/selectors/userSelectors';
import {
    editOrderItemSourcingActions,
    getCanEditSuppliers,
    getTotalQuantity,
} from 'features/EditOrderItemSourcing';
import { formatNumber } from 'shared/lib/formatNumber/formatNumber';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { OrderItemQuantityPicker } from 'shared/ui/OrderItemQuantityPicker';
import { OrderPriceDisplay } from 'shared/ui/OrderPriceDisplay/OrderPriceDisplay';
import { SupplierLogo } from 'shared/ui/SupplierLogo';
import { Tooltip } from 'shared/ui/Tooltip';
import { SupplierRejectedTooltip } from 'shared/ui/Tooltip/ui/SupplierRejectedTooltip';
import { SupplierVerifyingTooltip } from 'shared/ui/Tooltip/ui/SupplierVerifyingTooltip';

import {
    proposalIsOutOfStock,
    proposalIsOverStock,
    proposalSupplierIsInRejected,
    proposalSupplierIsInVerifying,
} from '../../lib';
import { columnStyles } from '../OrderViewItemSupplierTable.styles';

interface OrderViewItemSupplierTableRowProps {
    proposal: HydratedProposal;
    selectedItem: CatalogItem;
    selectedOrder: Order;
    getSupplierQuantity: (proposalUid: string) => number;
}

export const OrderViewItemSupplierTableRow = memo(
    ({
        proposal,
        selectedItem,
        selectedOrder,
        getSupplierQuantity,
    }: OrderViewItemSupplierTableRowProps) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();
        const canEditSuppliers = useSelector(getCanEditSuppliers);
        const isPreSubmission = useSelector(getSelectedOrderIsPreSubmission);
        const totalQuantity = useSelector(getTotalQuantity);
        const canEditQty = useSelector(getCanEditCountOfQty);
        const canSeePrices = useSelector(getUserCanSeePrices);
        const isMaintenanceTechnician = useSelector(
            getUserIsMaintenanceTechnician,
        );

        const isOutOfStock =
            isPreSubmission && proposalIsOutOfStock(proposal, selectedItem);

        const isOverStock =
            isPreSubmission &&
            proposalIsOverStock(proposal, getSupplierQuantity);

        const isInVerifying =
            isPreSubmission && proposalSupplierIsInVerifying(proposal);

        const isInRejected =
            isPreSubmission && proposalSupplierIsInRejected(proposal);

        const itemPrice = isPreSubmission
            ? proposal.sellPrice
            : proposal.selectedProposal?.price;

        const showPicker = isPreSubmission && canEditQty;
        const showPrices = canSeePrices && !isMaintenanceTechnician;

        // So the reason this exists is when we're in Item Details, we do not want to allow the user to decrease the quantity below 1 for the last allocation
        // They need to press the delete button instead
        // So we only set a 1 min quantity if the total quantity is 1 and the item quantity is 1
        const getMinQuantity = (uid: string) => {
            const itemQuantity = getSupplierQuantity(uid);
            if (totalQuantity === 1 && itemQuantity === 1) {
                return 1;
            }
            return 0;
        };

        return (
            <TableRow className={isOutOfStock ? 'out-of-stock' : ''}>
                <TableCell
                    className="table-suppliers"
                    sx={columnStyles.suppliers}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '14px',
                        }}
                    >
                        <SupplierLogo
                            vendor={proposal.hydratedVendor}
                            size={26}
                        />
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    lineHeight: '24px',
                                }}
                            >
                                {proposal.hydratedVendor?.title}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '7px',
                                }}
                            >
                                {isInVerifying && <SupplierVerifyingTooltip />}
                                {isInRejected && <SupplierRejectedTooltip />}
                                {proposal.isPreferred && (
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            padding: '2px 9px 2px 6px',
                                            alignItems: 'center',
                                            gap: '3px',
                                            borderRadius: '14px',
                                            background: '#DBEFE1',
                                            color: '#009F3A',
                                            fontSize: '10px',
                                            fontWeight: 700,
                                            marginTop: '4px',
                                        }}
                                    >
                                        <CheckOutlined
                                            sx={{
                                                fontSize: '10px',
                                            }}
                                        />
                                        {t('Preferred')}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </TableCell>
                {showPrices && (
                    <TableCell sx={columnStyles.unitCost}>
                        <OrderPriceDisplay
                            price={itemPrice}
                            order={selectedOrder}
                            orderHasLoadingPrices={
                                !selectedItem.isCheckFinished
                            }
                            width="60px"
                            containerProps={{
                                display: 'block',
                                textAlign: 'right',
                            }}
                            skeletonProps={{
                                margin: '0 auto',
                            }}
                        />
                    </TableCell>
                )}
                {isPreSubmission && (
                    <TableCell sx={columnStyles.stock}>
                        {isOutOfStock ? (
                            <Typography
                                sx={{ color: '#FF0000' }}
                                typography="openSans.body2"
                            >
                                {t('Out')}
                            </Typography>
                        ) : (
                            formatNumber(proposal.availableQuantity)
                        )}
                    </TableCell>
                )}
                <TableCell sx={columnStyles.qty}>
                    {isOverStock && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '-30px',
                                transform: 'translateY(-12px)',
                            }}
                        >
                            <Tooltip
                                isError
                                content={t(
                                    'Requested quantity exceeds available stock.',
                                )}
                            />
                        </Box>
                    )}
                    {showPicker ? (
                        <OrderItemQuantityPicker
                            value={getSupplierQuantity(proposal.uid)}
                            maxQuantity={proposal.availableQuantity}
                            minQuantity={getMinQuantity(proposal.uid)}
                            onQuantityChange={(newQuantity) => {
                                dispatch(
                                    editOrderItemSourcingActions.updateSupplierQuantity(
                                        {
                                            proposalUid: proposal.uid,
                                            quantity: newQuantity,
                                        },
                                    ),
                                );
                            }}
                            disabled={!canEditSuppliers}
                        />
                    ) : (
                        <Typography sx={{ textAlign: 'right' }}>
                            {proposal.selectedProposal?.selectedQuantity}
                        </Typography>
                    )}
                </TableCell>
                {showPrices && (
                    <TableCell sx={columnStyles.total}>
                        {getSupplierQuantity(proposal.uid) > 0 && (
                            <OrderPriceDisplay
                                price={
                                    getSupplierQuantity(proposal.uid) *
                                    itemPrice
                                }
                                order={selectedOrder}
                                orderHasLoadingPrices={
                                    !selectedItem.isCheckFinished
                                }
                                justifyContent="flex-end"
                                width="60px"
                            />
                        )}
                    </TableCell>
                )}
            </TableRow>
        );
    },
);
